import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import TradesPurpleLayout from '../components/Layout';
import { Helmet } from 'react-helmet-async';

export default function LegalPage() {
  const location = useLocation();

  // Scroll to the correct section when the page loads
  useEffect(() => {
    const hash = location.hash;
    if (hash) {
      const element = document.querySelector(hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <TradesPurpleLayout>
      <Helmet>
        <title>Privacy Policy & Terms of Service | TradesPurple</title>
        <meta name="description" content="TradesPurple privacy policy and terms of service" />
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Table of Contents */}
        <div className="mb-8 p-4 border-2 border-purple-200 rounded-lg bg-purple-50">
          <h1 className="text-2xl font-bold text-purple-900 mb-4">Legal Information</h1>
          <nav className="space-y-2">
            <a href="#privacy" className="block text-purple-700 hover:text-purple-900 transition-colors">Privacy Policy</a>
            <a href="#terms" className="block text-purple-700 hover:text-purple-900 transition-colors">Terms of Service</a>
          </nav>
        </div>

        {/* Privacy Policy Section */}
        <section id="privacy" className="mb-12">
          <h2 className="text-2xl font-bold text-purple-900 mb-6">Privacy Policy</h2>
          <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="space-y-6 text-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">1. Introduction</h3>
              <p>TradesPurple ("we", "our", or "us") respects your privacy and is committed to protecting your personal data. This privacy policy explains how we collect, use, and protect your information when you use our service.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">2. Data We Collect</h3>
              <p>We collect the following types of data:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Email address (for authentication and communication)</li>
                <li>Name (optional, for personalization)</li>
                <li>Postcode (to show local tradespeople)</li>
                <li>Information about tradespeople you save (names, contact details, notes)</li>
                <li>Usage data (how you interact with the service)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">3. How We Use Your Data</h3>
              <p>We use your data to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Provide and maintain our service</li>
                <li>Show you local tradespeople based on your postcode</li>
                <li>Enable you to save and share your trusted tradespeople</li>
                <li>Send service-related communications</li>
                <li>Improve our service</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">4. Data Sharing</h3>
              <p>We do not sell your personal data. We only share data:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>With your explicit consent (e.g., when you share your list)</li>
                <li>To comply with legal obligations</li>
                <li>With service providers who help us operate TradesPurple (under strict data protection agreements)</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">5. Your Rights (GDPR)</h3>
              <p>Under GDPR, you have the right to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Access your personal data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion of your data</li>
                <li>Object to data processing</li>
                <li>Data portability</li>
                <li>Withdraw consent at any time</li>
              </ul>
              <p className="mt-2">To exercise these rights, contact us at maxtattonbrown+tradespurple@gmail.com</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">6. Data Security</h3>
              <p>We implement appropriate technical and organizational measures to protect your data, including encryption and secure servers. However, no internet transmission is 100% secure.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">7. Cookies</h3>
              <p>We use essential cookies to make our service work. We don't use tracking or advertising cookies.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">8. Contact Us</h3>
              <p>For privacy concerns or questions, email us at: maxtattonbrown+tradespurple@gmail.com</p>
            </div>
          </div>
        </section>

        {/* Terms of Service Section */}
        <section id="terms" className="mb-12">
          <h2 className="text-2xl font-bold text-purple-900 mb-6">Terms of Service</h2>
          <p className="text-gray-600 mb-6">Last updated: {new Date().toLocaleDateString()}</p>

          <div className="space-y-6 text-gray-700">
            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">1. Acceptance of Terms</h3>
              <p>By using TradesPurple, you agree to these terms. If you don't agree, please don't use our service.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">2. Service Description</h3>
              <p>TradesPurple is a free platform that helps you save, contact, and share trusted tradespeople with your community.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">3. User Responsibilities</h3>
              <p>You agree to:</p>
              <ul className="list-disc ml-6 mt-2 space-y-1">
                <li>Provide accurate information</li>
                <li>Not share false or misleading information about tradespeople</li>
                <li>Respect others' privacy</li>
                <li>Not use the service for illegal activities</li>
                <li>Not attempt to harm or disrupt the service</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">4. Content</h3>
              <p>You retain ownership of content you create. By sharing tradespeople information, you grant us a license to display it to other users as intended by the service.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">5. Disclaimer</h3>
              <p>TradesPurple is provided "as is" without warranties. We don't verify tradespeople or guarantee their work. Users are responsible for their own due diligence.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">6. Limitation of Liability</h3>
              <p>We're not liable for any damages arising from your use of TradesPurple or interactions with tradespeople found through our service.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">7. Changes to Terms</h3>
              <p>We may update these terms. We'll notify users of significant changes via email or in-app notification.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">8. Termination</h3>
              <p>We may terminate or suspend your account for violations of these terms. You may delete your account at any time.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">9. Governing Law</h3>
              <p>These terms are governed by the laws of the European Union and the country where TradesPurple operates.</p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-purple-800 mb-2">10. Contact</h3>
              <p>For questions about these terms, email: maxtattonbrown+tradespurple@gmail.com</p>
            </div>
          </div>
        </section>
      </div>
    </TradesPurpleLayout>
  );
}
